import { Col, Form, Input, Row } from "antd";
import { t } from "../../translate";
import styles from "./email-input.module.scss";

export type EmailInputProps = {
    initialValue?: string;
    type?: "email" | "phone";
};

// This is a "validator factory". It's a function that returns a validator function.
// This allows us to pass the 'type' prop into our validation logic.
const createValidator = (type?: "email" | "phone") => {
    // This is the actual validator function that Ant Design will use.
    return (_: any, value: string) => {
        if (!value) {
            // The 'required' rule will handle the empty case, but this is a safeguard.
            return Promise.resolve(); 
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        // Allows for phone numbers with an optional leading 0, followed by 10 digits.
        const phoneRegex = /^(0?[1-9][0-9]{9})$/;

        switch (type) {
            case "email":
                if (!emailRegex.test(value)) {
                    // Use a specific error message for email
                    return Promise.reject(t("login.email-required"));
                }
                break;
            case "phone":
                if (!phoneRegex.test(value)) {
                    // Use a specific error message for phone
                    return Promise.reject(t("login.phone-required"));
                }
                break;
            default:
                // If type is undefined, check for both email and phone
                if (!emailRegex.test(value) && !phoneRegex.test(value)) {
                    return Promise.reject(t("login.email-or-phone-required"));
                }
                break;
        }

        // If all checks pass, resolve the promise.
        return Promise.resolve();
    };
};


const EmailInput = (props: EmailInputProps) => {
    const { type, initialValue } = props;

    // Make UI text dynamic based on the type for better user experience
    let label = t("login.email-or-phone.input-label");
    let placeholder = t("login.email-or-phone.input-placeholder");
    let requiredMessage = t("login.email-or-phone-required");

    if (type === "email") {
        label = t("login.email.input-label");
        placeholder = t("login.email.input-placeholder");
        requiredMessage = t("login.email-required");
    } else if (type === "phone") {
        label = t("login.phone.input-label");
        placeholder = t("login.phone.input-placeholder");
        requiredMessage = t("login.phone-required");
    }

    return (
        <Form.Item
            name="email"
            label={label}
            initialValue={initialValue}
            validateTrigger={['onBlur', 'onChange']}
            rules={[
                {
                    required: true,
                    message: requiredMessage,
                },
                {
                    // Call the factory to get the correct validator
                    validator: createValidator(type),
                },
            ]}
        >
            <Row>
                <Col xs={24} className={styles.inputContainer}>
                    <Input placeholder={placeholder} />
                </Col>
            </Row>
        </Form.Item>
    );
};

export default EmailInput;